import { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import parse from 'html-react-parser'
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  fetchWebinarEvents,
  WebinarEvent,
} from '@/features/resources/resources.actions'

import {
  Button,
  Card,
  CardColors,
  Text,
  Separator,
  Icon,
  Loader,
  Tooltip,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

const WEBINARS_TO_SHOW = 3

export const WebinarCard = () => {
  const dispatch = useAppDispatch()
  const [fetching, setFetching] = useState(true)
  const [webinars, setWebinars] = useState<WebinarEvent[]>()
  const [showMore, setShowMore] = useState(false)

  useEffect(() => {
    const fetch = async () => {
      setFetching(true)
      const res = await fetchWebinarEvents()(dispatch)
      setWebinars(res)
    }
    fetch()
    setFetching(false)
  }, [dispatch])

  // Filter out past events
  const filteredEvents = useMemo(
    () =>
      webinars?.filter(
        (event: WebinarEvent) =>
          DateTime.fromISO(event.start.dateTime) > DateTime.now()
      ),
    [webinars]
  )

  useEffect(() => {
    // Hide show more if we are under limit
    if (filteredEvents && filteredEvents.length <= WEBINARS_TO_SHOW) {
      setShowMore(true)
    }
  }, [filteredEvents, filteredEvents?.length])

  const eventsToShow = useMemo(() => {
    return showMore
      ? filteredEvents
      : filteredEvents?.slice(0, WEBINARS_TO_SHOW)
  }, [filteredEvents, showMore])

  return (
    <Card color={CardColors.LEVEL_0}>
      {fetching && <Loader />}

      <div className="flex flex-col">
        <div className="flex justify-between items-center">
          <Text variant={TypographyStyles.H2} className="w-7/8">
            Member Events
          </Text>

          <div className="hidden lg:flex items-center w-1/8">
            <Tooltip>
              <Tooltip.Trigger asChild>
                <Icon icon={faQuestionCircle} className="cursor-pointer" />
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Text variant={TypographyStyles.H3} className="mb-2">
                  Webinars
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  Here&apos;s an overview of our upcoming webinars. Please
                  register for any that catch your interest.
                </Text>
              </Tooltip.Content>
            </Tooltip>
          </div>
        </div>

        {!eventsToShow ||
          (eventsToShow.length === 0 && (
            <Text variant={TypographyStyles.BODY_XS} className="mt-4">
              No upcoming webinars. Check back soon!
            </Text>
          ))}

        {eventsToShow &&
          eventsToShow.length > 0 &&
          // Unclear what we need
          // eventsToShow.map(({ title, description, start, end, url }) => {
          eventsToShow.map(({ title, description, start }) => {
            const localDate = DateTime.fromISO(start.dateTime).setZone(
              start.timeZone
            )

            return (
              <div key={title} className="mt-4">
                <div className="flex justify-between">
                  <div className="space-y-2 w-7/8 overflow-hidden">
                    <Text variant={TypographyStyles.H3}>{title}</Text>
                    <Text variant={TypographyStyles.BODY_XS}>
                      {parse(description)}
                    </Text>

                    <Text
                      variant={TypographyStyles.BODY_XS}
                      color={TextColors.MOSS}
                    >
                      {localDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
                    </Text>

                    <Text
                      variant={TypographyStyles.BODY_XS}
                      color={TextColors.MOSS}
                    >
                      {localDate.toFormat(DATE_FORMATS_LUXON.TIME)}
                    </Text>
                  </div>

                  {/* TODO: Add back in when we have a calendar integration */}
                  {/* <div className="w-1/8">
                    <Select
                      variant="text"
                      options={[
                        {
                          value: 'add-to-calendar',
                          text: 'Add to Calendar',
                          onClick: () =>
                            window.open(
                              `https://calendar.google.com/calendar/u/0/r/eventedit?text=${encodeURI(
                                title
                              )}&dates=${localDate.toISO({
                                format: 'basic',
                              })}%2F${DateTime.fromISO(end.dateTime)
                                .setZone(end.timeZone)
                                .toISO({
                                  format: 'basic',
                                })}&location=${encodeURI(url)}`,
                              '_newtab'
                            ),
                        },
                      ]}
                    />
                  </div> */}
                </div>
              </div>
            )
          })}
        {!showMore && filteredEvents && (
          <>
            <Separator className="mx-4" />
            <Button
              variant="link"
              className="mt-4"
              onClick={() => setShowMore(true)}
            >
              View more
            </Button>
          </>
        )}
      </div>
    </Card>
  )
}
